/*
 * Copyright (c) 2025-2026.  atagon GmbH.  All rights reserved.
 *
 * This file is part of the atagon codebase and may not be copied,
 *  distributed, or modified without explicit written permission from
 * atagon GmbH. https://www.atagon.com
 */

import React, {useEffect, useState} from 'react';
import ButtonComponent from "./BasicInputs/ButtonComponent.jsx";
import Unique from "toolbox/unique";
import {ApiTokenAPI} from "../APIs/ApiTokenApi.js";
import InlineDialog from "./InlineDialog.jsx";
import CopyToClipboardComponent from "./CopyToClipboardComponent.jsx";
import TextInputFormComponent from "./FormComponents/TextInputFormComponent.jsx";
import IconButtonComponent from "./BasicInputs/IconButtonComponent.jsx";
import EmptyStateBig from "./EmptyState/EmptyStateBig.jsx";
import TextElement from "./TextElement.jsx";
import RowDataTable from "./RowDataTable.jsx";
import DisplayIcon from "./Icons/DisplayIcon.jsx";
import {useParameter} from "hooks/useParameter";
import Strings from "toolbox/strings";

const AccessToken = () => {
    const [newTokenName, setNewTokenName] = useParameter("newTokenName");

    const [allToken, setAllToken] = useState([]);
    const [tokenName, setTokenName] = useState("");
    const [newToken, setNewToken] = useState("");
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [isResultDialogOpen, setIsResultDialogOpen] = useState(false);

    const fetchData = async () => {
        let tokens = await ApiTokenAPI.listAll();
        setAllToken(tokens);
    };

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        if (!Strings.isEmpty(newTokenName)) {
            setTokenName(newTokenName);
            setIsCreateDialogOpen(true);
        }
    }, [newTokenName]);

    const openCreateDialog = () => {
        setTokenName(""); // Reset the name input
        setIsCreateDialogOpen(true);
    };

    const handleCreateToken = async () => {
        const finalTokenName = tokenName.trim() !== "" ? tokenName : `New API Key ${Unique.newInternalId()}`;
        let result = await ApiTokenAPI.create(finalTokenName);

        setNewToken(result.tokenValue);

        await fetchData();

        setIsCreateDialogOpen(false);
        setIsResultDialogOpen(true);
    };

    return (
        <>
            <ButtonComponent text={"Create new secret key"} iconName={"PiPlus"} className={"mb-4"}
                             onClick={async () => {
                                 openCreateDialog();
                             }}/>

            <InlineDialog
                title={"Create new API key"}
                isOpen={isCreateDialogOpen}
                button={[{
                    type: "save", text: "Create API Key", onClick: async (state, setState, close) => {
                        await handleCreateToken();
                    }
                }, {
                    type: "abort",
                    text: "Abbrechen",
                    onClick: async (inputState, setState, close) => {
                        setIsCreateDialogOpen(false);
                    }
                }]}
                onClose={() => {
                    setIsCreateDialogOpen(false);
                    setNewTokenName("")
                }}>
                <div className={"space-y-5"}>
                    <div className="text-sm text-gray-500">
                        Please enter a name for your new API Key.
                    </div>
                    <TextInputFormComponent name={"Name"}
                                            isRequired={false}
                                            placeholder={"My new Key"}
                                            value={tokenName}
                                            onChange={async (value) => {
                                                setTokenName(value);
                                            }}/>

                </div>
            </InlineDialog>

            <InlineDialog
                title={"API key generated"}
                isOpen={isResultDialogOpen}
                button={[{
                    type: "button",
                    text: "OK",
                    onClick: async (inputState, setState, close) => {
                        setIsResultDialogOpen(false);
                    }
                }]}
                onClose={() => {
                    setIsResultDialogOpen(false);
                }}>
                <div className={"space-y-5"}>
                    <div className="text-sm">
                        Please save this secret key somewhere safe and accessible. For security reasons, <span
                        className={"font-bold"}>you won't be able to view it again </span> through your atagon account.
                        If you lose this secret key, you will need to generate a new one.

                    </div>
                    <CopyToClipboardComponent text={newToken} className={"border rounded-lg h-10"}/>
                </div>
            </InlineDialog>

            <div className={"space-y-3 mt-2"}>
                <RowDataTable
                    iconElement={(row) => {
                        return (
                            <div className={`flex-shrink-0 p-1 flex justify-center items-center rounded border mr-3`}>
                                <DisplayIcon iconName={"PiKey"} size={"1.1rem"}/>
                            </div>
                        );
                    }}
                    buttons={(row) => {
                        return (
                            <>
                                <IconButtonComponent text={"Remove Token"}
                                                     iconName={"PiTrash"}
                                                     onClick={async () => {
                                                         await ApiTokenAPI.remove(row.indexKey);
                                                         await fetchData();
                                                     }}/>
                            </>
                        );
                    }}
                    data={allToken.map(item => ({
                        ...item,
                        Name: (
                            <div>
                                <div className={"font-semibold"}>
                                    {item.tokenName ?? "Access Token"}
                                </div>
                                <div className={"text-xs font-normal!"}>
                                    Secret Key: {item.tokenValue}
                                </div>
                            </div>
                        )
                    }))}
                    emptyState={() => {
                        return (
                            <EmptyStateBig iconName={"IoIosSearch"}
                                           text={
                                               <div className={"w-96 space-y-3"}>
                                                   <TextElement textType={"header-lg"}>
                                                       No API Keys found.
                                                   </TextElement>
                                                   <ButtonComponent text={"Create new secret key"} iconName={"PiPlus"}
                                                                    onClick={async () => {
                                                                        openCreateDialog();
                                                                    }}/>
                                               </div>
                                           }/>
                        );
                    }}
                    progressPending={false}
                />
            </div>
        </>
    );
};
AccessToken.propTypes = {};

AccessToken.defaultProps = {};

export default AccessToken;
